import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MenuLeaf, MenuNavigatorService } from '../../../core/services/menu-navigator.service';

interface PaletteSection {
  title: string;
  items: { leaf: MenuLeaf; index: number }[];
}

@Component({
  selector: 'app-menu-command-palette',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './menu-command-palette.component.html',
})
export class MenuCommandPaletteComponent {
  @ViewChild('paletteInput') paletteInput?: ElementRef<HTMLInputElement>;
  @ViewChild('paletteList') paletteList?: ElementRef<HTMLElement>;

  readonly nav = inject(MenuNavigatorService);
  private readonly router = inject(Router);

  readonly query = signal('');
  readonly activeIndex = signal(0);

  readonly sections = computed<PaletteSection[]>(() => {
    const q = this.query().trim();
    const sections: PaletteSection[] = [];
    let index = 0;
    const push = (title: string, leaves: MenuLeaf[]) => {
      if (!leaves.length) return;
      sections.push({ title, items: leaves.map((leaf) => ({ leaf, index: index++ })) });
    };

    if (!q) {
      const recents = this.nav.recentLeaves();
      const recentIds = new Set(recents.map((l) => l.id));
      push('เปิดล่าสุด', recents);
      push('ชื่นชอบ', this.nav.favoriteLeaves().filter((l) => !recentIds.has(l.id)));
      return sections;
    }

    const found = this.nav.search(q, 40);
    const headings = [...new Set(found.map((l) => l.heading))];
    headings.forEach((h) => push(h || 'เมนู', found.filter((l) => l.heading === h)));
    return sections;
  });

  readonly flatResults = computed<MenuLeaf[]>(() =>
    this.sections().flatMap((s) => s.items.map((i) => i.leaf)),
  );

  // ================= Normal Functions =================

  constructor() {
    effect(() => {
      if (this.nav.paletteOpen()) {
        this.query.set('');
        this.activeIndex.set(0);
        setTimeout(() => this.paletteInput?.nativeElement.focus());
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.nav.togglePalette();
      return;
    }
    if (!this.nav.paletteOpen()) return;

    const total = this.flatResults().length;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.nav.closePalette();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!total) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex.set((this.activeIndex() + step + total) % total);
      this.scrollActiveIntoView();
    } else if (event.key === 'Enter') {
      const leaf = this.flatResults()[this.activeIndex()];
      if (leaf) {
        event.preventDefault();
        this.open(leaf);
      }
    }
  }

  onQueryInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(0);
    if (this.paletteList) this.paletteList.nativeElement.scrollTop = 0;
  }

  setActive(index: number): void {
    if (this.activeIndex() !== index) this.activeIndex.set(index);
  }

  open(leaf: MenuLeaf): void {
    this.nav.closePalette();
    this.router.navigateByUrl(leaf.url);
  }

  isFavorite(id: number): boolean {
    return this.nav.favoriteIds().includes(id);
  }

  /** แบ่งข้อความเป็นช่วงที่ตรง / ไม่ตรงคำค้น เพื่อไฮไลต์โดยไม่ใช้ innerHTML */
  highlight(text: string): { text: string; hit: boolean }[] {
    const terms = this.query().toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!terms.length || !text) return [{ text, hit: false }];
    const lower = text.toLowerCase();
    const marks = new Array<boolean>(text.length).fill(false);
    for (const t of terms) {
      let from = 0;
      let at: number;
      while ((at = lower.indexOf(t, from)) >= 0) {
        marks.fill(true, at, at + t.length);
        from = at + t.length;
      }
    }
    const parts: { text: string; hit: boolean }[] = [];
    for (let i = 0; i < text.length; i++) {
      const last = parts[parts.length - 1];
      if (last && last.hit === marks[i]) last.text += text[i];
      else parts.push({ text: text[i], hit: marks[i] });
    }
    return parts;
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      const el = this.paletteList?.nativeElement.querySelector(`[data-idx="${this.activeIndex()}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  // ================= API Functions =================
}
